import { env } from '@/lib/env'
import { ApiError } from './errors'

/**
 * The single HTTP client for the Laravel API.
 *
 * Authentication is Sanctum's cookie-based SPA mode: the session lives in an
 * httpOnly cookie JavaScript cannot read, and state-changing requests carry
 * the XSRF-TOKEN cookie back as a header. This module owns that handshake so
 * no feature code ever has to think about it.
 */

type JsonBody = Record<string, unknown> | readonly unknown[]

export interface RequestOptions extends Omit<RequestInit, 'method' | 'body'> {
  /** Serialised as JSON with the matching Content-Type. */
  json?: JsonBody
  /** Raw body — for FormData uploads. Ignored when `json` is set. */
  body?: BodyInit
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

/**
 * Sent as Accept-Language on every request, so the API localizes the data
 * it owns (tenant names, plan names, attention wording). Updated
 * synchronously by the language switch, before any refetch it triggers.
 */
let requestLanguage = 'en'

export function setRequestLanguage(language: string): void {
  requestLanguage = language
}

/**
 * One in-flight CSRF bootstrap, shared by every caller. Without memoising,
 * a screen that fires five mutations at once would make five identical
 * /sanctum/csrf-cookie round trips first.
 */
let csrfCookie: Promise<void> | null = null

function ensureCsrfCookie(): Promise<void> {
  csrfCookie ??= fetch(`${env.apiUrl}/sanctum/csrf-cookie`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then((response) => {
      if (!response.ok) {
        throw new ApiError(response.status, 'Could not initialise the CSRF cookie.', null)
      }
    })
    .catch((error: unknown) => {
      // Do not cache a failure: the next call should try again.
      csrfCookie = null
      throw error
    })

  return csrfCookie
}

function readCookie(name: string): string | null {
  const match = document.cookie
    .split('; ')
    .find((pair) => pair.startsWith(`${name}=`))

  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text()

  if (text === '') {
    return null
  }

  return response.headers.get('Content-Type')?.includes('application/json')
    ? (JSON.parse(text) as unknown)
    : text
}

/** Sends a request with the session's CSRF handshake; the caller reads the response. */
async function send(method: string, path: string, options: RequestOptions = {}, isRetry = false): Promise<Response> {
  const { json, body: rawBody, headers: initHeaders, ...init } = options

  if (!SAFE_METHODS.has(method)) {
    await ensureCsrfCookie()
  }

  const headers = new Headers(initHeaders)
  headers.set('Accept', 'application/json')
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', requestLanguage)
  }
  // Laravel treats this as an AJAX request: JSON errors, never a redirect
  // to a login page the SPA does not have.
  headers.set('X-Requested-With', 'XMLHttpRequest')

  const xsrf = readCookie('XSRF-TOKEN')
  if (xsrf !== null) {
    headers.set('X-XSRF-TOKEN', xsrf)
  }

  let body: BodyInit | undefined = rawBody
  if (json !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(json)
  }

  const response = await fetch(`${env.apiUrl}${path}`, {
    ...init,
    method,
    headers,
    body,
    credentials: 'include',
  })

  // 419 means the CSRF token expired — the session rotated or timed out
  // while the tab sat idle. Refresh it and retry exactly once; a second 419
  // is a real failure and must surface rather than loop.
  if (response.status === 419 && !isRetry) {
    csrfCookie = null
    await ensureCsrfCookie()

    return send(method, path, options, true)
  }

  return response
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(method, path, options)

  if (response.status === 204) {
    return undefined as T
  }

  const payload = await parseBody(response)

  if (!response.ok) {
    throw ApiError.from(response.status, payload)
  }

  return payload as T
}

/** A file the API streamed — a CSV export, a ZIP bundle — and the name it gave it. */
export interface DownloadedFile {
  blob: Blob
  filename: string | null
  /** The response's headers — an export says how many rows it holds (X-Export-Rows). */
  headers: Headers
}

/** `attachment; filename="x.csv"` (or RFC 5987's `filename*=UTF-8''x.csv`) → x.csv */
function filenameOf(disposition: string | null): string | null {
  if (disposition === null) return null

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1]
  if (encoded !== undefined) return decodeURIComponent(encoded.trim())

  return /filename="?([^";]+)"?/i.exec(disposition)?.[1]?.trim() ?? null
}

async function file(path: string, options: RequestOptions = {}): Promise<DownloadedFile> {
  const response = await send('GET', path, options)

  if (!response.ok) {
    throw ApiError.from(response.status, await parseBody(response))
  }

  // The bytes as sent: a CSV keeps its byte-order mark (Excel needs it for Arabic), a ZIP stays binary.
  return { blob: await response.blob(), filename: filenameOf(response.headers.get('Content-Disposition')), headers: response.headers }
}

/** Hands a downloaded file to the browser to save. */
export function saveFile({ blob, filename }: Pick<DownloadedFile, 'blob' | 'filename'>, fallback: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename ?? fallback
  link.click()
  // Give the browser a moment to start the download before the URL goes.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),

  post: <T>(path: string, json?: JsonBody, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, json }),

  put: <T>(path: string, json?: JsonBody, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, json }),

  patch: <T>(path: string, json?: JsonBody, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, json }),

  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),

  /** A download through the same session and headers — errors come back as ApiError, like any request. */
  file: (path: string, options?: RequestOptions) => file(path, options),
} as const
